import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  fecharAlerta,
  getSnapshot,
  limparAlertas,
  pushAlerta,
  substituirAlerta,
  subscribe,
} from '../components/shared/alertaStore';

const ALERTA_INICIAL = { id: null, mensagem: '', tipo: 'info', titulo: null, aberto: false };

const extrairMensagemErro = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.mensagem ||
  error?.response?.data?.error ||
  error?.message ||
  (typeof error === 'string' ? error : 'Ocorreu um erro');

/**
 * Hook do sistema único de alertas (store global + `AlertaHost`).
 *
 * Mantém a API antiga (`alerta`, `setAlerta`, `showError`, `showSuccess`,
 * `hideAlerta`) para não quebrar os consumidores existentes.
 */
export const useAlerta = () => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const atual = snapshot.fila[0] || null;

  const showAlerta = useCallback(
    (mensagem, tipo = 'info', opcoes = {}) => pushAlerta(mensagem, tipo, opcoes),
    []
  );

  const showSuccess = useCallback(
    (mensagem, opcoes = {}) => pushAlerta(mensagem, 'success', opcoes),
    []
  );

  const showWarning = useCallback(
    (mensagem, opcoes = {}) => pushAlerta(mensagem, 'warning', opcoes),
    []
  );

  const showInfo = useCallback((mensagem, opcoes = {}) => pushAlerta(mensagem, 'info', opcoes), []);

  const showError = useCallback(
    (error, opcoes = {}) => pushAlerta(extrairMensagemErro(error), 'error', opcoes),
    []
  );

  const hideAlerta = useCallback(() => fecharAlerta(), []);

  /** Compatibilidade: `setAlerta({ mensagem, tipo, aberto })` ou updater. */
  const setAlerta = useCallback((valor) => {
    const atualAgora = getSnapshot().fila[0] || ALERTA_INICIAL;
    const proximo = typeof valor === 'function' ? valor(atualAgora) : valor;

    if (!proximo || proximo.aberto === false || !proximo.mensagem) {
      fecharAlerta();
      return null;
    }

    return substituirAlerta(proximo.mensagem, proximo.tipo, proximo);
  }, []);

  const alerta = useMemo(
    () => ({
      ...(atual || ALERTA_INICIAL),
      aberto: Boolean(atual),
    }),
    [atual]
  );

  return {
    alerta,
    fila: snapshot.fila,
    total: snapshot.fila.length,
    setAlerta,
    showAlerta,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    hideAlerta,
    limparAlertas,
  };
};

export default useAlerta;
